<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectDocumentsDialog from '@/components/app/cases/documents/SelectDocumentsDialog.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import SelectPdfFormsDialog from '@/components/pdf/SelectPdfFormsDialog.vue'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { useToast } from '@/components/ui/toast'
  import {
    DocumentTypeIdEnum,
    useEmailPopover,
    type AttachedDocument,
    type FormDocumentProperties,
    type QuestionnaireDocumentProperties
  } from '@/composables/useEmailPopover'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import {
    CasesService,
    EmailTemplatesService,
    FormsService,
    QuestionnairesService,
    type CustomerEmailTemplate,
    type TEmailTemplate,
    type TForm,
    type TQuestionnaire
  } from '@/types/generated'
  import {
    CheckCircle,
    Eye,
    FileQuestion,
    FileText,
    Mail,
    Mails,
    Maximize2,
    Minimize2,
    Pencil,
    Search,
    X,
    XCircle
  } from 'lucide-vue-next'
  import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      newEmail: 'New Email',
      subject: 'Subject',
      selectTemplate: 'Select Template',
      attach: 'Attach',
      send: 'Send',
      previewEmail: 'Preview Email',
      discard: 'Discard',
      questionnairesAndDocs: 'Questionnaires and Documents',
      documentTemplate: 'Document Template',
      questionnaire: 'Questionnaire',
      maximize: 'Maximize',
      reset: 'Reset',
      close: 'Close',
      sendingEmail: 'Sending Email...',
      attachDocuments: 'Attach Documents',
      attachQuestionnaire: 'Attach Questionnaire',
      emptyAttachmentsText: 'Click "Attach" to add documents or questionnaires',
      remove: 'Remove',
      sending: 'Sending...',
      noResultsFound: 'No results found',
      searchTemplate: 'Search template',
      selectTemplateTitle: 'Select Template',
      selectTemplateDescription: 'Choose an email template from the list',
      name: 'Name',
      type: 'Type',
      legalArea: 'Legal Area',
      acceptance: 'Acceptance',
      rejection: 'Rejection',
      attachDocumentsTitle: 'Attach Documents',
      questionnaireTitle: 'Questionnaire',
      questionnaireDescription: 'Select one or more questionnaires',
      searchQuestionnaire: 'Search questionnaire',
      minimize: 'Minimize',
      success: 'Success',
      error: 'Error',
      emailSentSuccess: 'Email was sent successfully',
      emailSendError: 'Email could not be sent:'
    },
    de: {
      newEmail: 'Neue E-Mail',
      subject: 'Betreff',
      selectTemplate: 'Vorlage auswählen',
      attach: 'Anhängen',
      send: 'Senden',
      previewEmail: 'E-Mail Vorschau',
      discard: 'Verwerfen',
      questionnairesAndDocs: 'Fragebögen und Dokumente',
      documentTemplate: 'Dokumentenvorlage',
      questionnaire: 'Fragebogen',
      maximize: 'Maximieren',
      reset: 'Zurücksetzen',
      close: 'Schließen',
      sendingEmail: 'E-Mail wird gesendet...',
      attachDocuments: 'Dokumente anhängen',
      attachQuestionnaire: 'Fragebogen anhängen',
      emptyAttachmentsText: 'Klicken Sie auf "Anhängen" um Dokumente oder Fragebögen hinzuzufügen',
      remove: 'Entfernen',
      sending: 'Wird gesendet...',
      noResultsFound: 'Keine Ergebnisse gefunden',
      searchTemplate: 'Vorlage suchen',
      selectTemplateTitle: 'Vorlage auswählen',
      selectTemplateDescription: 'Wählen Sie eine E-Mail Vorlage aus der Liste',
      name: 'Name',
      type: 'Typ',
      legalArea: 'Rechtsgebiet',
      acceptance: 'Aufnahme',
      rejection: 'Ablehnung',
      attachDocumentsTitle: 'Dokumente anhängen',
      questionnaireTitle: 'Fragebogen',
      questionnaireDescription: 'Wählen Sie einen oder mehrere Fragebögen aus',
      searchQuestionnaire: 'Fragebogen suchen',
      minimize: 'Minimieren',
      success: 'Erfolg',
      error: 'Fehler',
      emailSentSuccess: 'E-Mail wurde erfolgreich versendet',
      emailSendError: 'E-Mail konnte nicht gesendet werden:'
    }
  })

  // Get the shared email popover state from the composable
  const emailPopover = useEmailPopover()

  const props = defineProps<{
    isOpen: boolean
  }>()

  const emit = defineEmits(['update:isOpen', 'emailSent'])

  // Type for selected document
  type SelectedDocument = (TForm | TQuestionnaire) & {
    document_type: 'document' | 'questionnaire'
  }

  const { toast } = useToast()
  const { translateLawField } = useLawFieldTranslation()

  // Use the shared state for email content
  const {
    isMinimized,
    isMaximized,
    popoverPosition,
    popoverSize,
    replyData,
    emailSubject,
    emailContent,
    selectedTemplate: sharedSelectedTemplate,
    attachedDocuments: sharedAttachedDocuments,
    setEmailSubject,
    setEmailContent,
    updateEmailData,
    addAttachedDocument,
    removeAttachedDocument,
    clearEmailData
  } = emailPopover

  // Local state that doesn't need to persist across tabs
  const startDrag = ref(false)
  const dragOffset = ref({ x: 0, y: 0 })
  const resizing = ref(false)
  const resizeStartPos = ref({ x: 0, y: 0 })
  const resizeStartSize = ref({ width: 580, height: 550 })
  const emailTemplates = ref<TEmailTemplate[]>([])
  const selectedDocument = ref<TForm | TQuestionnaire | null>(null)
  const isUpdatingTemplate = ref(false)

  const selectedTemplate = computed(() => sharedSelectedTemplate.value as TEmailTemplate | null)

  // Attached documents
  const selectedAttachments = computed({
    get: () => sharedAttachedDocuments.value as AttachedDocument[],
    set: (value) => {
      // Clear current documents
      sharedAttachedDocuments.value = []
      // Add new ones
      value.forEach((doc) => addAttachedDocument(doc))
    }
  })

  const loading = ref(false)
  const isDropdownOpen = ref(false)
  const searchQuery = ref('')
  const selectTriggerRef = ref<HTMLElement | null>(null)

  // Store references
  const { user } = useUserStore()
  const caseStore = useCaseStore()
  const newPdfStore = useNewPdfStore()

  // Document states
  const showDocumentDialog = ref(false)
  const showQuestionnaireDialog = ref(false)
  const questionnaires = ref<TQuestionnaire[]>([])
  const loadingQuestionnaires = ref(false)
  const showAttachMenu = ref(false)
  const showPdfEditor = ref(false)
  const sendingEmail = ref(false)
  const showTemplateDialog = ref(false)

  // Pagination state for questionnaires
  const questionnaireCurrentPage = ref(1)
  const questionnaireTotalCount = ref(0)
  const questionnairePageSize = 20
  const questionnaireSearchQuery = ref('')

  const filteredTemplates = computed(() => {
    if (!searchQuery.value) return emailTemplates.value
    const query = searchQuery.value.toLowerCase()
    return emailTemplates.value.filter(
      (template) =>
        template.name.toLowerCase().includes(query) ||
        template.subject.toLowerCase().includes(query)
    )
  })

  watch(
    () => replyData.value,
    (newReplyData) => {
      if (newReplyData) {
        emailSubject.value = newReplyData.subject
      }
    },
    { immediate: true }
  )

  const hasDocumentPlaceholder = computed(() => {
    if (!emailContent.value) return false
    return emailContent.value.includes('data-key="questionnaires_and_documents"')
  })

  const isFormValid = computed(() => {
    const hasRequiredFields = true
    // If template contains document placeholder but no documents are attached
    if (hasDocumentPlaceholder.value && selectedAttachments.value.length === 0) {
      return false
    }

    return hasRequiredFields
  })

  // Popover style computed properties
  const popoverStyle = computed(() => {
    if (isMinimized.value) {
      return {
        position: 'fixed' as const,
        bottom: '20px',
        right: '20px',
        width: '380px',
        height: '60px',
        transform: 'none',
        zIndex: 50
      }
    }

    if (isMaximized.value) {
      return {
        position: 'fixed' as const,
        top: '20px',
        left: '20px',
        right: '20px',
        bottom: '20px',
        width: 'auto',
        height: 'auto',
        transform: 'none',
        zIndex: 50
      }
    }

    return {
      position: 'fixed' as const,
      top: `${popoverPosition.y}px`,
      left: `${popoverPosition.x}px`,
      width: `${popoverSize.width}px`,
      height: `${popoverSize.height}px`,
      zIndex: 50
    }
  })

  const contentStyle = computed(() => {
    return isMinimized.value ? { display: 'none' } : {}
  })

  // Email methods
  const fetchEmailTemplates = async () => {
    if (emailTemplates.value.length > 0) return
    try {
      loading.value = true
      const response = await EmailTemplatesService.emailTemplatesList({ limit: 0 })
      emailTemplates.value = response.results
    } catch (error) {
      genericErrorToast()
    } finally {
      loading.value = false
    }
  }

  const fetchQuestionnaires = async (page: number = 1, searchQuery?: string) => {
    try {
      loadingQuestionnaires.value = true
      const offset = (page - 1) * questionnairePageSize
      const response = await QuestionnairesService.questionnairesList({
        limit: questionnairePageSize,
        offset,
        search: searchQuery || undefined
      })
      questionnaires.value = response.results || []
      questionnaireTotalCount.value = response.count || 0
      questionnaireCurrentPage.value = page
    } catch (error) {
      genericErrorToast()
    } finally {
      loadingQuestionnaires.value = false
    }
  }

  const handleQuestionnairePage = async (page: number) => {
    await fetchQuestionnaires(page, questionnaireSearchQuery.value || undefined)
  }

  const handleQuestionnaireSearch = async (query: string) => {
    questionnaireSearchQuery.value = query
    await fetchQuestionnaires(1, query || undefined)
  }

  // Modified methods that use the shared state
  const toggleMinimize = () => {
    emailPopover.toggleMinimize()
  }

  const toggleMaximize = () => {
    emailPopover.toggleMaximize()
  }

  const closePopover = () => {
    // Update the parent component's state
    emit('update:isOpen', false)
    // Reset email data
    emailPopover.clearEmailData()
    // Also update the shared state
    emailPopover.closePopover()
  }

  const startDragging = (event: MouseEvent) => {
    if (isMaximized.value) return
    startDrag.value = true
    dragOffset.value = {
      x: event.clientX - popoverPosition.x,
      y: event.clientY - popoverPosition.y
    }
    document.addEventListener('mousemove', onDrag)
    document.addEventListener('mouseup', stopDragging)
  }

  const onDrag = (event: MouseEvent) => {
    if (startDrag.value) {
      const newX = event.clientX - dragOffset.value.x
      const newY = event.clientY - dragOffset.value.y
      emailPopover.updatePosition(newX, newY)
    }
  }

  const stopDragging = () => {
    startDrag.value = false
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDragging)
  }

  const startResizing = (event: MouseEvent) => {
    resizing.value = true
    resizeStartPos.value = {
      x: event.clientX,
      y: event.clientY
    }
    resizeStartSize.value = {
      width: popoverSize.width,
      height: popoverSize.height
    }
    document.addEventListener('mousemove', onResize)
    document.addEventListener('mouseup', stopResizing)
  }

  const onResize = (event: MouseEvent) => {
    if (resizing.value) {
      const deltaX = event.clientX - resizeStartPos.value.x
      const deltaY = event.clientY - resizeStartPos.value.y

      const newWidth = Math.max(480, resizeStartSize.value.width + deltaX)
      const newHeight = Math.max(480, resizeStartSize.value.height + deltaY)

      emailPopover.updateSize(newWidth, newHeight)
    }
  }

  const stopResizing = () => {
    resizing.value = false
    document.removeEventListener('mousemove', onResize)
    document.removeEventListener('mouseup', stopResizing)
  }

  // Set initial popover position at the right side of the screen
  const initPopoverPosition = () => {
    emailPopover.resetPosition()
  }

  // Event listeners
  onMounted(() => {
    // Reset position whenever the component is mounted
    initPopoverPosition()
    fetchEmailTemplates()

    fetchQuestionnaires()

    // Sync the component's isOpen prop with the global state
    // watch(
    //   () => props.isOpen,
    //   (newValue) => {
    //     if (newValue) {
    //       emailPopover.openPopover()
    //       // Reset position when opening
    //       initPopoverPosition()
    //     } else {
    //       emailPopover.closePopover()
    //     }
    //   },
    //   { immediate: true }
    // )

    // Watch for external state changes and emit events accordingly
    // watch(
    //   () => emailPopover.isOpen.value,
    //   (newValue) => {
    //     emit('update:isOpen', newValue)
    //   }
    // )

    // document.addEventListener('keydown', (event) => {
    //   if (event.key === 'Escape' && props.isOpen) {
    //     closePopover()
    //   }
    // })
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', (event) => {
      if (event.key === 'Escape' && props.isOpen) {
        closePopover()
      }
    })
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDragging)
    document.removeEventListener('mousemove', onResize)
    document.removeEventListener('mouseup', stopResizing)
  })

  // Handle click outside
  const emailPopoverRef = ref<HTMLElement | null>(null)

  const openTemplateDialog = () => {
    showTemplateDialog.value = true
  }

  // Key change: Refactored template selection to avoid reactivity loops
  const handleTemplateSelect = (template: CustomerEmailTemplate) => {
    // First, close the dialog
    showTemplateDialog.value = false
    emailPopover.setTemplate(template as any)
  }

  const toggleSelect = () => {
    isDropdownOpen.value = !isDropdownOpen.value
  }

  // Simplified menu handling
  const handleAttachClick = () => {
    showAttachMenu.value = !showAttachMenu.value
  }

  const handleDocumentClick = () => {
    showDocumentDialog.value = true
    showAttachMenu.value = false
  }

  const handleQuestionnaireClick = () => {
    showQuestionnaireDialog.value = true
    showAttachMenu.value = false
  }

  watch(
    () => caseStore.caseRecord?.data?.client,
    (client) => {
      if (client) {
        newPdfStore.setPrefillData({
          client_first_name: client.first_name ?? '',
          client_last_name: client.last_name ?? '',
          client_email: client.email ?? '',
          client_phone: client.phone ?? '',
          probably_client_city: client.city ?? '',
          probably_client_zip: client.zip ?? '',
          probably_client_street: client.street ?? '',
          probably_client_street_number: client.street_number ?? '',
          probably_client_date_of_birth: client.date_of_birth ?? ''
        })
      }
    },
    { immediate: true }
  )

  const handleDocumentsSelect = async (documents: TForm[]) => {
    // First, remove all the PDF template documents that are not in the new selection
    selectedAttachments.value = selectedAttachments.value.filter((attachment) => {
      // Keep non-PDF documents
      if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
        return true
      }

      // For PDF documents, check if they're in the new selection
      const formProps = attachment.documentProperties as FormDocumentProperties
      return documents.some((doc) => doc.id === formProps.id)
    })

    // Then add any new documents that aren't already attached
    const currentFormIds = selectedAttachments.value
      .filter((doc) => doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .map((doc) => (doc.documentProperties as FormDocumentProperties).id)

    // Find documents that aren't already attached
    const newDocuments = documents.filter((doc) => !currentFormIds.includes(doc.id))

    // Convert new documents to the attachment format and add them
    if (newDocuments.length > 0) {
      const convertedDocs = newDocuments.map((doc) => {
        return {
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: doc.id,
            organisation: doc.organisation,
            created_at: doc.created_at,
            modified_at: doc.modified_at,
            tag: doc.tag,
            title: doc.title,
            description: doc.description,
            file: doc.file,
            due_at: doc.due_at,
            document_name: doc.document_name,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties
        } as AttachedDocument
      })

      // Create a new array to ensure reactivity
      selectedAttachments.value = [...selectedAttachments.value, ...convertedDocs]

      // Load PDFs using the batch loader
      if (newDocuments.length > 0) {
        newPdfStore
          .batchLoadPdfs(newDocuments)
          .then((results) => {
            selectedAttachments.value.forEach((attachment: AttachedDocument) => {
              attachment.documentProperties =
                attachment.documentProperties as FormDocumentProperties
              if (
                attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
                results[attachment.documentProperties.id] === true
              ) {
                const currentAnnotations = newPdfStore.getAnnotations(
                  attachment.documentProperties.id
                )
                attachment.documentProperties.annotationData = [...currentAnnotations]
              }
            })
          })
          .catch((error) => {
            console.error('Error in batch loading PDFs:', error)
          })
      }
    }
  }
  const handleQuestionnairesSelect = async (questionnaires: TQuestionnaire[]) => {
    // Add selected questionnaires to the list
    selectedAttachments.value = [
      ...selectedAttachments.value,
      ...questionnaires.map((questionnaire) => {
        const questionnaireWithUuidAsId = {
          ...questionnaire,
          id: questionnaire.uuid
        }
        return {
          documentProperties: questionnaireWithUuidAsId,
          documentType: {
            id: DocumentTypeIdEnum.QUESTIONNAIRE,
            name: 'Fragebogen'
          }
        } as AttachedDocument
      })
    ]
  }

  const handleFormPreview = async (form: FormDocumentProperties) => {
    if (form.file) {
      window.open(form.file, '_blank')
    }
  }

  const openPdfEditor = (form: AttachedDocument) => {
    selectedDocument.value = form.documentProperties as TForm
    showPdfEditor.value = true
  }

  const handleQuestionnairePreview = async (questionnaire: QuestionnaireDocumentProperties) => {
    const preview_url = `${window.location.origin}/client/portal/questionnaire/preview/${questionnaire.uuid}`
    window.open(preview_url, '_blank')
  }

  const removeDocument = (attachment: AttachedDocument) => {
    // Get the document identifier based on its type
    let docIdentifier: string | undefined

    if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
      docIdentifier = (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid
    } else {
      docIdentifier = (attachment.documentProperties as FormDocumentProperties)?.id
    }

    if (!docIdentifier) {
      console.error('Could not determine document identifier for removal', attachment)
      return
    }

    selectedAttachments.value = selectedAttachments.value.filter((attachment) => {
      if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
        return (
          (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid !== docIdentifier
        )
      } else {
        return (attachment.documentProperties as FormDocumentProperties)?.id !== docIdentifier
      }
    })

    // Also remove from the PDF store if it was loaded
    if (
      attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
      docIdentifier &&
      newPdfStore.selectedPdfs.has(docIdentifier)
    ) {
      newPdfStore.removePdf(docIdentifier)
    }
  }

  const handleSendEmail = async () => {
    if (sendingEmail.value) return

    try {
      sendingEmail.value = true

      // Ensure all PDF annotations are up to date in the attachments
      const pdfAttachments = selectedAttachments.value.filter(
        (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
      )

      for (const attachment of pdfAttachments) {
        const formProps = attachment.documentProperties as FormDocumentProperties
        const currentAnnotations = newPdfStore.getAnnotations(formProps.id)
        formProps.annotationData = [...currentAnnotations]
      }

      // Make the API call
      const response = await CasesService.casesSendEmailCreate({
        uuid: caseStore.caseRecord?.uuid || '',
        requestBody: {
          subject: emailSubject.value,
          body: emailContent.value,
          attachments: selectedAttachments.value,
          auto_reject: selectedTemplate.value?.type === 'reject'
        }
      })

      // Show success message
      toast({
        title: t('success'),
        description: t('emailSentSuccess'),
        variant: 'default'
      })

      // Reset form using the shared state
      clearEmailData()
      selectedAttachments.value = []

      // Close popover
      closePopover()

      // Emit event to notify parent
      emit('emailSent', response)

      // Also emit through the event bus for other components
      emailPopover.emitEmailSent(response)
    } catch (error: any) {
      toast({
        title: t('error'),
        description: t('emailSendError') + '\n' + JSON.stringify(error.body),
        variant: 'destructive'
      })
    } finally {
      sendingEmail.value = false
    }
  }

  const getAttachmentKey = (attachment: any): string => {
    if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
      return `questionnaire-${attachment.documentProperties?.uuid}`
    } else if (attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      return `form-${attachment.id}`
    } else {
      return `other-${attachment.uuid || attachment.id}`
    }
  }

  const formatAttachmentName = (attachment: AttachedDocument): string => {
    if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
      return attachment.documentProperties?.title || ''
    }
    if (attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      const props = attachment.documentProperties as FormDocumentProperties
      return props.title || props.document_name.replace(/^forms\/org\/[^/]+\//, '')
    }
    return ''
  }
  onUnmounted(() => {
    newPdfStore.clear()
  })
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="emailPopoverRef"
      :style="popoverStyle"
      class="flex flex-col rounded-lg bg-white shadow-2xl transition-all duration-200 ease-in-out"
      :class="{
        'border border-purple-200 shadow-lg hover:border-purple-300 hover:shadow-xl': isMinimized,
        'shadow-2xl': !isMinimized
      }"
    >
      <!-- Header with controls -->
      <div
        class="flex cursor-move items-center justify-between px-4"
        :class="{
          'h-full rounded-lg border-none bg-gradient-to-r from-purple-50 to-purple-100':
            isMinimized,
          'h-12 rounded-b-none rounded-t-lg border-b border-gray-200 bg-gray-50': !isMinimized
        }"
        @mousedown="startDragging"
      >
        <div class="flex items-center gap-3">
          <Mail
            v-if="isMinimized"
            class="h-6 w-6 text-purple-600"
          />
          <span
            class="font-medium"
            :class="{
              'text-base font-semibold text-purple-700': isMinimized,
              'text-sm text-gray-700': !isMinimized
            }"
          >
            {{ isMinimized ? t('newEmail') : selectedTemplate?.name || t('newEmail') }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="!isMinimized"
            variant="ghost"
            size="icon"
            :title="isMaximized ? t('reset') : t('maximize')"
            @click.stop="toggleMaximize"
          >
            <Maximize2 class="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            :class="{
              'text-primary hover:bg-purple-100 hover:text-primary-hover': isMinimized
            }"
            :title="isMinimized ? t('maximize') : t('minimize')"
            @click.stop="toggleMinimize"
          >
            <Minimize2
              v-if="!isMinimized"
              class="h-4 w-4"
            />
            <Maximize2
              v-else
              class="h-5 w-5"
            />
          </Button>
          <!-- <button
            class="rounded p-1.5 hover:bg-opacity-80"
            :class="{
              'text-purple-600 hover:bg-purple-100 hover:text-red-600': isMinimized,
              'text-gray-500 hover:bg-red-100 hover:text-red-700': !isMinimized
            }"
            :title="t('close')"
            @click.stop="toggleMinimize"
          >
            <ChevronDown
              v-if="isMinimized"
              class="h-5 w-5"
            />
            <ChevronUp
              v-else
              class="h-4 w-4"
            />
          </button> -->
        </div>
      </div>

      <!-- Email content - hide when minimized -->
      <div
        v-if="!isMinimized"
        class="flex flex-grow flex-col overflow-hidden"
      >
        <div class="flex-grow space-y-4 overflow-auto p-4">
          <!-- Loading Overlay -->
          <div
            v-if="sendingEmail"
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/80"
          >
            <div class="flex flex-col items-center gap-3">
              <div
                class="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"
              ></div>
              <p class="text-sm font-medium text-gray-600">{{ t('sendingEmail') }}</p>
            </div>
          </div>

          <!-- Email Template Selection -->
          <div class="grid grid-cols-5 gap-4">
            <!-- Subject -->
            <div class="col-span-3">
              <InputField
                v-model="emailSubject"
                :placeholder="t('subject')"
                name="subject"
                class="w-full py-2.5"
              />
            </div>
            <div class="col-span-2">
              <Button
                variant="secondary"
                @click="openTemplateDialog"
              >
                <div class="flex items-center">
                  <Mails class="mr-2" />
                  <span class="text-black-500 truncate">
                    {{ selectedTemplate?.name || t('selectTemplate') }}
                  </span>
                </div>
              </Button>
            </div>
          </div>

          <!-- Content -->
          <div class="mb-4">
            <RichTextEditor
              v-model="emailContent"
              :enable-placeholder-button="true"
              :enabled-context-aware-placeholders="['questionnaires_and_documents']"
              :enable-text-styling-buttons="true"
              :font-color="user?.org?.email_text_color"
              :font-size="user?.org?.email_text_size"
              :font-family="user?.org?.email_text_font"
              :full-height="false"
              class="min-h-[160px]"
            />
          </div>

          <!-- Documents Section -->
          <div class="rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
            <!-- Header -->
            <div
              class="flex items-center justify-between rounded-t-xl border-b border-gray-200 bg-gray-50 px-4 py-2"
            >
              <h3 class="text-sm font-medium">
                {{ selectedAttachments.length }} {{ t('questionnairesAndDocs') }}
              </h3>
              <DropdownMenu v-model="showAttachMenu">
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    class="text-xs font-medium"
                  >
                    <span>+ {{ t('attach') }}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent class="w-56">
                  <DropdownMenuItem @click="handleDocumentClick">
                    <FileText class="mr-2 h-4 w-4" />
                    <span>{{ t('documentTemplate') }}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="handleQuestionnaireClick">
                    <FileQuestion class="mr-2 h-4 w-4" />
                    <span>{{ t('questionnaire') }}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <!-- Empty State -->
            <div
              v-if="selectedAttachments.length === 0"
              class="flex items-center justify-center p-6 text-center"
            >
              <p class="text-xs text-gray-500">
                {{ t('emptyAttachmentsText') }}
              </p>
            </div>

            <!-- Attachment List -->
            <div
              v-else
              class="max-h-[230px] divide-y divide-gray-200 overflow-y-auto"
            >
              <div
                v-for="attachment in selectedAttachments"
                :key="getAttachmentKey(attachment)"
                class="flex items-center justify-between px-4 py-2"
              >
                <div class="flex items-center gap-2">
                  <component
                    :is="
                      attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE
                        ? FileQuestion
                        : FileText
                    "
                    class="h-4 w-4 shrink-0"
                    :class="{
                      'text-red-500':
                        attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE,
                      'text-cyan-700':
                        attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE
                    }"
                  />
                  <span class="w-[200px] truncate text-sm">
                    {{ formatAttachmentName(attachment) }}
                  </span>
                </div>
                <div class="flex items-center">
                  <div v-if="attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE">
                    <Button
                      variant="ghost"
                      size="icon"
                      @click="
                        handleFormPreview(attachment.documentProperties as FormDocumentProperties)
                      "
                    >
                      <Eye class="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      @click="openPdfEditor(attachment)"
                    >
                      <Pencil class="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    v-if="attachment.documentType.id === DocumentTypeIdEnum.QUESTIONNAIRE"
                    variant="ghost"
                    size="icon"
                    @click="
                      handleQuestionnairePreview(
                        attachment.documentProperties as QuestionnaireDocumentProperties
                      )
                    "
                  >
                    <Eye class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="text-red-500 hover:bg-red-50 hover:text-red-600"
                    @click="removeDocument(attachment)"
                  >
                    <X class="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with action buttons -->
        <div class="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-4">
          <Button
            variant="secondary"
            size="sm"
            class="h-8"
            @click="closePopover"
          >
            {{ t('discard') }}
          </Button>
          <div class="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              class="h-8 bg-purple-600 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!isFormValid || sendingEmail"
              @click="handleSendEmail"
            >
              <div
                v-if="sendingEmail"
                class="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"
              ></div>
              {{ sendingEmail ? t('sending') : t('send') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Resize handle - hide when minimized -->
      <div
        v-if="!isMinimized && !isMaximized"
        class="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        @mousedown.stop="startResizing"
      >
        <div class="absolute bottom-1 right-1 h-3 w-3 border-b-2 border-r-2 border-gray-400"></div>
      </div>
    </div>
  </Teleport>

  <!-- Document Selection Dialogs -->
  <SelectPdfFormsDialog
    v-model:show="showDocumentDialog"
    :title="t('attachDocumentsTitle')"
    :initial-selected-documents="
      selectedAttachments
        .filter((doc) => doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE)
        .map((doc) => {
          const formProps = doc.documentProperties as FormDocumentProperties
          // Create a TForm object from the document properties
          return {
            id: formProps.id,
            organisation: formProps.organisation,
            created_at: formProps.created_at,
            modified_at: formProps.modified_at,
            tag: formProps.tag,
            title: formProps.title,
            description: formProps.description,
            file: formProps.file,
            due_at: formProps.due_at,
            document_name: formProps.document_name
          } as TForm
        })
    "
    @select="handleDocumentsSelect"
  />

  <SelectDocumentsDialog
    v-model="showQuestionnaireDialog"
    :title="t('questionnaireTitle')"
    :description="t('questionnaireDescription')"
    :items="questionnaires"
    :is-loading="loadingQuestionnaires"
    :search-placeholder="t('searchQuestionnaire')"
    :total-count="questionnaireTotalCount"
    :current-page="questionnaireCurrentPage"
    :page-size="questionnairePageSize"
    @select-items="handleQuestionnairesSelect"
    @preview-item="handleQuestionnairePreview"
    @page-change="handleQuestionnairePage"
    @search="handleQuestionnaireSearch"
  />

  <!-- Email Template Selection Dialog - Similar to SelectDocumentsDialog -->
  <Dialog
    v-model:open="showTemplateDialog"
    @close="showTemplateDialog = false"
  >
    <DialogContent class="sm:max-w-[800px]">
      <DialogHeader>
        <DialogTitle>{{ t('selectTemplateTitle') }}</DialogTitle>
        <DialogDescription>{{ t('selectTemplateDescription') }}</DialogDescription>
      </DialogHeader>

      <!-- Search Input -->
      <div class="relative w-full">
        <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <Search class="h-4 w-4 text-gray-500" />
        </div>
        <input
          v-model="searchQuery"
          type="text"
          class="block w-full rounded-lg border-gray-300 ps-10 focus:border-purple-500 focus:ring-purple-500"
          :placeholder="t('searchTemplate')"
        />
      </div>

      <!-- Templates List -->
      <div class="max-h-[400px] overflow-y-auto py-4">
        <div
          v-if="loading"
          class="flex items-center justify-center py-4"
        >
          <div
            class="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"
          ></div>
        </div>
        <div
          v-else-if="filteredTemplates.length === 0"
          class="flex items-center justify-center py-4"
        >
          <p class="text-sm text-gray-500">{{ t('noResultsFound') }}</p>
        </div>
        <table
          v-else
          class="w-full table-fixed border-collapse"
        >
          <thead class="sticky top-0 z-10 bg-white">
            <tr class="border-b border-gray-200">
              <th class="w-1/4 px-3 py-2 text-left text-sm font-medium text-gray-500">
                {{ t('name') }}
              </th>
              <th class="w-1/3 px-3 py-2 text-left text-sm font-medium text-gray-500">
                {{ t('subject') }}
              </th>
              <th class="w-1/6 px-3 py-2 text-left text-sm font-medium text-gray-500">
                {{ t('type') }}
              </th>
              <th class="w-1/6 px-3 py-2 text-left text-sm font-medium text-gray-500">
                {{ t('legalArea') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="template in filteredTemplates"
              :key="template.uuid"
              class="cursor-pointer hover:bg-gray-50"
              :class="{ 'bg-gray-50': template.uuid === selectedTemplate?.uuid }"
              @click="handleTemplateSelect(template as CustomerEmailTemplate)"
            >
              <td class="px-3 py-2 text-sm">
                <span class="block truncate font-medium">{{ template.name }}</span>
              </td>
              <td class="px-3 py-2 text-sm">
                <span class="block truncate text-gray-600">{{ template.subject }}</span>
              </td>
              <td class="px-3 py-2 text-sm">
                <div class="flex items-center gap-2">
                  <component
                    :is="template.type === 'create' ? CheckCircle : XCircle"
                    class="h-4 w-4 shrink-0"
                    :class="template.type === 'create' ? 'text-green-600' : 'text-red-600'"
                  />
                  <span class="truncate text-gray-600">
                    {{ template.type === 'create' ? t('acceptance') : t('rejection') }}
                  </span>
                </div>
              </td>
              <td class="px-3 py-2 text-sm">
                <span class="block truncate text-gray-600">
                  {{ translateLawField(template.law_field) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DialogContent>
  </Dialog>

  <!-- PDF Editor Modal -->
  <div class="flex h-full w-full">
    <PDFViewer
      v-model:isOpen="showPdfEditor"
      :filename="selectedDocument?.title || ''"
      :url="(selectedDocument as TForm)?.file || ''"
      :hide-navigation="false"
      :annotation="true"
      :form-id="(selectedDocument as TForm)?.id || ''"
    />
  </div>
</template>
